import type { VendaDoProduto } from './vendasDoProduto'

// Tela do cliente (design aprovado por ela, 24/09 noite, sobre o print que ela
// mandou): o historico de UM cliente com UM produto. Janela de 12, 6 ou 3
// meses ate o mes atual; 3 quadros comparados em NUMERO com a janela anterior
// de mesmo tamanho; 3 graficos por mes. Tocar num mes: quadros e lista so
// daquele mes e o "antes" some -- a media mensal continua sendo a da janela,
// porque e a regua para comparar o mes.

export type Janela = 12 | 6 | 3

export interface Totais {
  faturamento: number
  pedidos: number
  unidades: number
}

export interface BarraDoCliente extends Totais {
  chave: string
  rotulo: string
}

export interface VisaoDoCliente {
  meses: string[]
  barras: BarraDoCliente[]
  // null quando o tocado nao e da janela (trocou de 12 para 3, por exemplo).
  mesTocado: string | null
  resumo: Totais
  // null com um mes tocado (escolha dela, a mesma da ficha).
  anterior: Totais | null
  media: Totais
  lista: VendaDoProduto[]
}

function chaveDoMes(ano: number, mes: number): string {
  const d = new Date(ano, mes, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function mesesDaJanela(hoje: Date, n: number, recuo = 0): string[] {
  const fim = hoje.getMonth() - recuo
  return Array.from({ length: n }, (_, k) => chaveDoMes(hoje.getFullYear(), fim - (n - 1) + k))
}

function nomeDoMes(chave: string): string {
  const [a, m] = chave.split('-').map(Number)
  return new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')
}

// "out/25 a set/26" -- embaixo das barras so cabe o mes, o ano vai aqui.
export function rotuloDaJanela(meses: string[]): string {
  const curto = (c: string) => `${nomeDoMes(c)}/${c.slice(2, 4)}`
  return `${curto(meses[0])} a ${curto(meses[meses.length - 1])}`
}

function totais(vendas: VendaDoProduto[]): Totais {
  return {
    faturamento: vendas.reduce((s, v) => s + v.valorTotal, 0),
    pedidos: new Set(vendas.map((v) => v.pedidoId)).size,
    unidades: vendas.reduce((s, v) => s + v.unidades, 0),
  }
}

const doMes = (vendas: VendaDoProduto[], chave: string) => vendas.filter((v) => v.data.startsWith(chave))
const dosMeses = (vendas: VendaDoProduto[], meses: string[]) => vendas.filter((v) => meses.includes(v.data.slice(0, 7)))

// `vendas` ja vem so deste cliente, da mais nova para a mais velha
// (carregarVendasDoProduto ordena assim).
export function visaoDoCliente(
  vendas: VendaDoProduto[], hoje: Date, janela: Janela, mesTocado: string | null,
): VisaoDoCliente {
  const meses = mesesDaJanela(hoje, janela)
  const mes = mesTocado !== null && meses.includes(mesTocado) ? mesTocado : null
  const daJanela = dosMeses(vendas, meses)
  const daJanelaTotais = totais(daJanela)
  const lista = mes === null ? daJanela : doMes(vendas, mes)
  return {
    meses,
    barras: meses.map((chave) => ({ chave, rotulo: nomeDoMes(chave), ...totais(doMes(vendas, chave)) })),
    mesTocado: mes,
    resumo: mes === null ? daJanelaTotais : totais(lista),
    anterior: mes === null ? totais(dosMeses(vendas, mesesDaJanela(hoje, janela, janela))) : null,
    media: {
      faturamento: daJanelaTotais.faturamento / janela,
      pedidos: daJanelaTotais.pedidos / janela,
      unidades: daJanelaTotais.unidades / janela,
    },
    lista,
  }
}

export interface ClienteDoPeriodo {
  clienteId: string
  nome: string
  unidades: number
  total: number
  compras: number
}

// A lista embaixo do Historico da ficha: uma linha por cliente (escolha dela,
// 24/09 noite), quem comprou mais em valor primeiro.
export function clientesDoPeriodo(vendas: VendaDoProduto[]): ClienteDoPeriodo[] {
  const porCliente = new Map<string, { nome: string; unidades: number; total: number; pedidos: Set<string> }>()
  for (const v of vendas) {
    const c = porCliente.get(v.clienteId) ?? { nome: v.clienteNome, unidades: 0, total: 0, pedidos: new Set<string>() }
    c.unidades += v.unidades
    c.total += v.valorTotal
    c.pedidos.add(v.pedidoId)
    porCliente.set(v.clienteId, c)
  }
  return [...porCliente]
    .map(([clienteId, c]) => ({ clienteId, nome: c.nome, unidades: c.unidades, total: c.total, compras: c.pedidos.size }))
    .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome, 'pt-BR'))
}

// Em cima da barra de faturamento, a 360px, "R$ 4.800" nao cabe: "4,8 mil".
export function abreviarValor(valor: number): string {
  if (valor === 0) return ''
  if (valor < 1000) return String(Math.round(valor))
  return `${(valor / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
}
